import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import HiveLocationMap from "@/components/HiveLocationMap";
import { MapPin, Crosshair, Search, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Location card on the Hive Detail page.
 * - Read mode: shows resolved label + lat/lon + a small map preview.
 * - Edit mode: address search + GPS detect + drag-to-pin + save/cancel.
 */
export default function HiveLocationCard({ hive, onUpdated, canEdit = true }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    location: hive.location || "",
    latitude: hive.latitude ?? null,
    longitude: hive.longitude ?? null,
  });
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  const enterEdit = () => {
    setDraft({
      location: hive.location || "",
      latitude: hive.latitude ?? null,
      longitude: hive.longitude ?? null,
    });
    setEditing(true);
  };

  const setCoords = (lat, lon, label) => {
    setDraft((d) => ({
      ...d,
      latitude: lat == null ? null : Number(lat.toFixed(5)),
      longitude: lon == null ? null : Number(lon.toFixed(5)),
      ...(label ? { location: label } : {}),
    }));
  };

  const lookupAddress = async () => {
    const q = (draft.location || "").trim();
    if (q.length < 2) {
      toast.error("Type a city or address first");
      return;
    }
    setGeocoding(true);
    try {
      const res = await api.get("/weather/geocode", { params: { q } });
      setCoords(res.data.latitude, res.data.longitude, res.data.label || q);
      toast.success(`Found: ${res.data.label}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't find that location");
    } finally {
      setGeocoding(false);
    }
  };

  const detectLocation = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Geolocation isn't available in this browser");
      return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const res = await api.get("/weather/reverse-geocode", { params: { lat: latitude, lon: longitude } });
          setCoords(res.data.latitude, res.data.longitude, res.data.label || draft.location);
          toast.success(res.data.label ? `Pinned: ${res.data.label}` : "Coordinates pinned");
        } catch {
          setCoords(latitude, longitude);
          toast.success("Coordinates pinned");
        } finally {
          setDetecting(false);
        }
      },
      (err) => {
        setDetecting(false);
        toast.error(err.code === 1
          ? "Location permission denied. Type an address or pin on the map."
          : "Couldn't detect your location. Type an address instead.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        location: draft.location || "",
        latitude: draft.latitude == null || draft.latitude === "" ? null : Number(draft.latitude),
        longitude: draft.longitude == null || draft.longitude === "" ? null : Number(draft.longitude),
      };
      const res = await api.patch(`/hives/${hive.id}`, payload);
      toast.success("Location updated");
      onUpdated?.(res.data);
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const hasCoords = (editing ? draft.latitude : hive.latitude) != null;
  const lat = editing ? draft.latitude : hive.latitude;
  const lon = editing ? draft.longitude : hive.longitude;
  const label = editing
    ? draft.location
    : (hive.weather_location_label || hive.location || "");

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="hive-location-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-amber-700" />
          <h2 className="font-display text-xl font-medium">Location</h2>
          {hasCoords && !editing && (
            <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
              pinned
            </span>
          )}
        </div>
        {canEdit && !editing && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full border-stone-300"
            onClick={enterEdit}
            data-testid="hive-location-edit-button"
          >
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
          </Button>
        )}
        {editing && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-stone-300"
              onClick={() => setEditing(false)}
              disabled={saving}
              data-testid="hive-location-cancel-button"
            >
              <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
            </Button>
            <Button
              size="sm"
              className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300"
              onClick={save}
              disabled={saving}
              data-testid="hive-location-save-button"
            >
              <Check className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        )}
      </div>

      {editing && (
        <div className="space-y-3 mb-4">
          <div className="flex gap-2">
            <Input
              value={draft.location}
              onChange={(e) => setDraft({ ...draft, location: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupAddress(); } }}
              placeholder="City, address, or landmark"
              maxLength={200}
              data-testid="hive-location-card-input"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={lookupAddress}
              disabled={geocoding || !draft.location?.trim()}
              title="Find on map"
              className="shrink-0"
              data-testid="hive-location-card-lookup"
            >
              <Search className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={detectLocation}
              disabled={detecting}
              title="Use my current location"
              className="shrink-0"
              data-testid="hive-location-card-detect"
            >
              <Crosshair className={`w-4 h-4 ${detecting ? "animate-pulse" : ""}`} />
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-stone-500">Latitude</Label>
              <Input
                type="number"
                step="0.00001"
                min={-90}
                max={90}
                value={draft.latitude ?? ""}
                onChange={(e) => setDraft({ ...draft, latitude: e.target.value === "" ? null : Number(e.target.value) })}
                placeholder="—"
                data-testid="hive-location-card-lat"
              />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Longitude</Label>
              <Input
                type="number"
                step="0.00001"
                min={-180}
                max={180}
                value={draft.longitude ?? ""}
                onChange={(e) => setDraft({ ...draft, longitude: e.target.value === "" ? null : Number(e.target.value) })}
                placeholder="—"
                data-testid="hive-location-card-lon"
              />
            </div>
          </div>
          <p className="text-xs text-stone-500">
            Click the map or drag the pin to adjust. Coordinates power the weather forecast and seasonal reminders.
          </p>
        </div>
      )}

      {!editing && (
        <div className="text-sm text-stone-700 mb-3">
          {label ? (
            <div>
              <div className="font-medium text-stone-900">{label}</div>
              {hasCoords && (
                <div className="text-xs text-stone-500 mt-0.5 font-mono">
                  {Number(lat).toFixed(4)}, {Number(lon).toFixed(4)}
                </div>
              )}
            </div>
          ) : (
            <div className="text-stone-500">
              No location set yet. Add one to unlock weather forecasts and seasonal reminders.
            </div>
          )}
        </div>
      )}

      {hasCoords ? (
        <HiveLocationMap
          latitude={lat}
          longitude={lon}
          readOnly={!editing}
          onChange={editing
            ? async (la, lo) => {
                setCoords(la, lo);
                try {
                  const res = await api.get("/weather/reverse-geocode", { params: { lat: la, lon: lo } });
                  if (res.data.label) setDraft((d) => ({ ...d, location: res.data.label }));
                } catch { /* silent */ }
              }
            : undefined}
          height={editing ? 240 : 200}
        />
      ) : editing ? (
        <HiveLocationMap
          latitude={null}
          longitude={null}
          onChange={async (la, lo) => {
            setCoords(la, lo);
            try {
              const res = await api.get("/weather/reverse-geocode", { params: { lat: la, lon: lo } });
              if (res.data.label) setDraft((d) => ({ ...d, location: res.data.label }));
            } catch { /* silent */ }
          }}
          height={240}
        />
      ) : null}
    </div>
  );
}
